/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import authManager from "../utils/authManager";
import { attachReferenceIdToError } from "../utils/requestCorrelation";
import { AUTHORIZATION_SERVICE_UNAVAILABLE_ERROR } from "./constants";

/**
 * Resolves the best human-readable message from a backend error payload.
 *
 * @param {string|object|null|undefined} payload - Parsed response body.
 * @param {string} defaultErrorMessage - Fallback message when no payload text exists.
 * @returns {string} The message that should be shown to the user.
 */
export const getErrorMessageFromPayload = (payload, defaultErrorMessage) => {
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return defaultErrorMessage;
  }

  if (typeof payload.error === "string") {
    return payload.error;
  }

  if (typeof payload.message === "string") {
    return payload.message;
  }

  if (typeof payload.detail === "string") {
    return payload.detail;
  }

  const baseErrors = payload.errors?.base;

  if (typeof baseErrors === "string") {
    return baseErrors;
  }

  if (Array.isArray(baseErrors) && typeof baseErrors[0] === "string") {
    return baseErrors[0];
  }

  return defaultErrorMessage;
};

/**
 * Reads the response body as text without consuming the original response twice.
 *
 * @param {Response|object|null|undefined} response - The failed fetch response.
 * @returns {Promise<string|null>} The raw text body, if available.
 */
export const readErrorResponseText = async (response) => {
  const clonedResponse =
    typeof response?.clone === "function" ? response.clone() : null;

  if (typeof clonedResponse?.text === "function") {
    try {
      const text = await clonedResponse.text();
      return text || null;
    } catch (error) {
      return null;
    }
  }

  if (typeof response?.text === "function") {
    try {
      const text = await response.text();
      return text || null;
    } catch (error) {
      return null;
    }
  }

  return null;
};

/**
 * Extracts a parsed error payload from a failed fetch response by reading the
 * body once and attempting to parse JSON from the text.
 *
 * @param {Response|object|null|undefined} response - The failed fetch response.
 * @returns {Promise<string|object|null>} Parsed JSON, raw text, or null.
 */
export const parseErrorPayload = async (response) => {
  const responseText = await readErrorResponseText(response);

  if (!responseText) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch (error) {
    return responseText;
  }
};

/**
 * Determines whether a parsed payload represents the authorization service outage.
 *
 * @param {string|object|null|undefined} payload - Parsed response payload.
 * @returns {boolean} True when the payload matches the known outage message.
 */
export const isAuthorizationServiceUnavailablePayload = (payload) => {
  const message =
    typeof payload === "string"
      ? payload
      : payload?.error || payload?.message || null;

  if (typeof message !== "string") {
    return false;
  }

  const normalizedMessage = message
    .replace(/\s*\(Reference ID: [^)]+\)\s*$/u, "")
    .trimEnd();

  return normalizedMessage === AUTHORIZATION_SERVICE_UNAVAILABLE_ERROR;
};

/**
 * Applies the shared authenticated-client error handling used by both axios and
 * fetch callers so reference ids, auth-service outages, and 401 logout rules do
 * not drift between transport implementations.
 *
 * @param {object} error - The transport error to normalize.
 * @param {string|null|undefined} requestId - The best available request id.
 * @param {object} [options={}] - Optional client-specific behavior.
 * @param {(error: object) => object} [options.buildUnauthorizedSilentError] - Optional
 * `/me`-specific 401 transformer used by the axios client.
 * @returns {object} The normalized error to throw or reject with.
 */
export const finalizeAuthenticatedApiError = (
  error,
  requestId,
  { buildUnauthorizedSilentError } = {},
) => {
  const enrichedError = attachReferenceIdToError(error, requestId);
  const resolvedRequestId = enrichedError?.requestId || requestId || null;

  if (isAuthorizationServiceUnavailablePayload(enrichedError?.response?.data)) {
    authManager.handleAuthorizationServiceUnavailable(resolvedRequestId);
    return enrichedError;
  }

  if (enrichedError?.response?.status === 401) {
    if (authManager.isAuthenticated() && !authManager.isLoggingOut) {
      authManager.logout();
    }

    if (
      typeof buildUnauthorizedSilentError === "function" &&
      enrichedError?.config?.url?.includes("/me")
    ) {
      return buildUnauthorizedSilentError(enrichedError);
    }
  }

  return enrichedError;
};
